import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaShieldAlt, FaServer, FaCode, FaArrowRight } from 'react-icons/fa';
import { motion } from 'motion/react';
import PageNavbar from './PageNavbar';
import Footer from './Footer';
import ScrollReveal from './ScrollReveal';
import { getRevealProps, staggerContainer, staggerItem, defaultViewport } from '../motion/scrollMotion';
import { servicesMenuColumns } from '../config/servicesMenuConfig';
import { useRegion } from '../context/RegionContext';
import './ContentPages.css';

// All category groups, flattened out of the 5 mega-menu columns.
const serviceGroups = servicesMenuColumns.flat();

// A random spot (kept inside the section so clusters don't clip at the edges).
const randomSpot = () => ({
  top: 6 + Math.random() * 78,
  left: 4 + Math.random() * 88,
});

// Spider-web geometry (constant): a center, 8 radial threads, and 3 concentric
// rings connecting the threads. Built once and reused for every web.
const WEB = (() => {
  const cx = 40;
  const cy = 40;
  const R = 34;
  const spokeCount = 8;
  const ringFracs = [0.42, 0.7, 1];

  const spokes = Array.from({ length: spokeCount }, (_, i) => {
    const a = (i / spokeCount) * Math.PI * 2 - Math.PI / 2;
    return { x: +(cx + R * Math.cos(a)).toFixed(1), y: +(cy + R * Math.sin(a)).toFixed(1), a };
  });

  const rings = ringFracs.map((f) =>
    spokes
      .map((s) => `${(cx + R * f * Math.cos(s.a)).toFixed(1)},${(cy + R * f * Math.sin(s.a)).toFixed(1)}`)
      .join(' ')
  );

  return { cx, cy, spokes, rings };
})();

// One spider web. It draws itself in thread-by-thread (radial threads first,
// then the rings, then the nodes), holds, then fades out. On each cycle the web
// is remounted (key={tick}) at a fresh random spot, so it spins up somewhere
// new — on loop.
const BoxCluster = () => {
  const cycleMs = useMemo(() => 6000 + Math.random() * 2000, []);
  const [tick, setTick] = useState(0);
  const [spot, setSpot] = useState(randomSpot);

  useEffect(() => {
    const id = setInterval(() => {
      setSpot(randomSpot());
      setTick((t) => t + 1);
    }, cycleMs);
    return () => clearInterval(id);
  }, [cycleMs]);

  return (
    <span
      key={tick}
      className="sd-cluster"
      style={{
        top: `${spot.top}%`,
        left: `${spot.left}%`,
        animationDuration: `${cycleMs}ms`,
      }}
    >
      <svg className="sd-web" viewBox="0 0 80 80" width="74" height="74">
        {/* radial threads — draw first, one after another */}
        {WEB.spokes.map((s, i) => (
          <line
            key={`s${i}`}
            x1={WEB.cx}
            y1={WEB.cy}
            x2={s.x}
            y2={s.y}
            className="web-line"
            style={{ animationDelay: `${i * 0.14}s` }}
          />
        ))}
        {/* concentric rings — draw after the threads */}
        {WEB.rings.map((points, k) => (
          <polygon
            key={`r${k}`}
            points={points}
            className="web-ring"
            style={{ animationDelay: `${1.25 + k * 0.34}s` }}
          />
        ))}
        {/* nodes — pop in last */}
        <circle cx={WEB.cx} cy={WEB.cy} r="1.5" className="web-node" style={{ animationDelay: '0.05s' }} />
        {WEB.spokes.map((s, i) => (
          <circle
            key={`n${i}`}
            cx={s.x}
            cy={s.y}
            r="1.3"
            className="web-node"
            style={{ animationDelay: `${2.45 + i * 0.06}s` }}
          />
        ))}
      </svg>
    </span>
  );
};

const services = [
  {
    icon: <FaShieldAlt />,
    title: 'Cybersecurity Services',
    desc: 'Penetration testing, compliance, cloud security, 24/7 SOC monitoring, and incident response.',
    href: '/cybersecurity',
  },
  {
    icon: <FaServer />,
    title: 'Managed IT Services',
    desc: 'Endpoint management, monitoring, backup & disaster recovery, and identity governance.',
    href: '/managed-it',
  },
  {
    icon: <FaCode />,
    title: 'Software Development',
    desc: 'Custom applications, AI solutions, secure SDLC, APIs, automation, and security dashboards.',
    href: '/development',
  },
];

const ServicesPage = () => {
  const { region } = useRegion();
  // Mirror the mega-menu rule: show an item if it has no region or matches the active one.
  const visible = (it) => !it.region || it.region === region;

  // Decorative looping clusters of small dark-orange boxes (see BoxCluster).
  const clusterIds = [0, 1, 2, 3];

  return (
  <div className="content-page">
    <PageNavbar />

    <section className="content-hero">
      <div className="content-hero-background" />
      <div className="content-hero-stars" />
      <ScrollReveal className="content-hero-inner" animateOnMount variant="fadeUp">
        <span className="content-badge">Services</span>
        <h1 className="content-hero-title">
          Integrated Security, IT &amp; <span className="content-accent">Engineering.</span>
        </h1>
        <p className="content-hero-desc">
          Three core disciplines under one roof — cybersecurity, managed IT, and secure software
          development — designed to close the gaps that fragmented vendors leave behind.
        </p>
      </ScrollReveal>
    </section>

    <section className="content-section">
      <div className="content-container">
        <motion.div className="content-section-header" {...getRevealProps('fadeUp')}>
          <span className="content-section-tag">What We Deliver</span>
          <h2 className="content-section-title">Choose Your Service Area</h2>
          <p className="content-section-subtitle">
            Explore our full portfolio across offensive security, IT operations, and secure engineering.
          </p>
        </motion.div>

        <motion.div
          className="content-grid"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={defaultViewport}
        >
          {services.map((service) => (
            <motion.div key={service.href} className="content-card" variants={staggerItem}>
              <div className="content-card-icon">{service.icon}</div>
              <h3 className="content-card-title">{service.title}</h3>
              <p className="content-card-desc">{service.desc}</p>
              <Link to={service.href} className="content-card-link">
                Learn more <FaArrowRight />
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>

    <section className="content-section alt services-directory-section">
      <div className="sd-boxfield" aria-hidden="true">
        {clusterIds.map((id) => (
          <BoxCluster key={id} />
        ))}
      </div>

      <div className="content-container">
        <motion.div className="content-section-header" {...getRevealProps('fadeUp')}>
          <span className="content-section-tag">Service Catalog</span>
          <h2 className="content-section-title">Explore Every Service</h2>
          <p className="content-section-subtitle">
            The complete list of services across every discipline — pick any one to see the details.
          </p>
        </motion.div>

        <motion.div
          className="services-directory-grid"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={defaultViewport}
        >
          {serviceGroups.map((group) => {
            const items = group.items.filter(visible);
            if (items.length === 0) return null;
            return (
              <motion.div key={group.title} className="services-directory-cat" variants={staggerItem}>
                <h3 className="services-directory-cat-title">{group.title}</h3>
                <ul className="services-directory-list">
                  {items.map((it) => (
                    <li key={it.slug}>
                      <Link to={it.href} className="services-directory-link">
                        <span>{it.label}</span>
                        <FaArrowRight />
                      </Link>
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>

    <Footer />
  </div>
  );
};

export default ServicesPage;
